import * as XLSX from "xlsx";
import { RawExcelRow, ParsedMachineIdentity, ParsedOperation } from "../types/operations";

// =============================================
// Supported "User" column format: Type_Number_AM/PM
// Examples: CNC_1_AM, Edge_2_PM, Saw_1_AM
// =============================================
const USER_COLUMN_PATTERN = /^([A-Za-z]+)_(\d+)_(AM|PM)$/i;

// Map parsed User prefixes to Postgres machine_type enum labels (CNC is all-caps).
function normaliseMachineTypeForEnum(rawType: string): string {
  const lower = rawType.toLowerCase();
  if (lower === "cnc") return "CNC";
  if (lower === "edge") return "Edge";
  if (lower === "saw") return "Saw";
  return rawType.charAt(0).toUpperCase() + rawType.slice(1).toLowerCase();
}

// =============================================
// Maximum plausible duration per operation.
// Values above this are capped to prevent
// outlier records from skewing aggregates.
// =============================================
const MAX_SECONDS_PER_PART = 180;
const OUTLIER_THRESHOLD_SECONDS = 30 * 60;

// =============================================
// Convert an Excel serial date number to an
// ISO 8601 timestamp string.
// The XLSX library handles both the 1900 and
// 1904 date systems automatically.
// =============================================
function excelSerialToIso(serial: number): string {
  const parsed = XLSX.SSF.parse_date_code(serial);

  if (!parsed) {
    throw new Error(`Could not parse Excel date serial: ${serial}`);
  }

  const date = new Date(
    Date.UTC(parsed.y, parsed.m - 1, parsed.d, parsed.H, parsed.M, parsed.S)
  );

  return date.toISOString();
}

// =============================================
// Parse a date value from the Excel row.
// Accepts either an Excel serial number or a
// date/time string in DD/MM/YYYY HH:MM:SS AM/PM
// format (as produced by some export tools).
// =============================================
function parseDateValue(rawValue: number | string): string {
  if (typeof rawValue === "number") {
    return excelSerialToIso(rawValue);
  }

  if (typeof rawValue === "string") {
    const trimmed = rawValue.trim();
    const pattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4}) (\d{1,2}):(\d{2}):(\d{2}) ([AP]M)$/i;
    const match = trimmed.match(pattern);

    if (match) {
      const [, dayStr, monthStr, yearStr, hourStr, minuteStr, secondStr, amPm] = match;

      let hour = parseInt(hourStr, 10);
      const isPm = amPm.toUpperCase() === "PM";

      if (isPm && hour !== 12) {
        hour += 12;
      } else if (!isPm && hour === 12) {
        hour = 0;
      }

      const date = new Date(
        Date.UTC(
          parseInt(yearStr, 10),
          parseInt(monthStr, 10) - 1,
          parseInt(dayStr, 10),
          hour,
          parseInt(minuteStr, 10),
          parseInt(secondStr, 10)
        )
      );

      return date.toISOString();
    }
  }

  throw new Error(`Unrecognised date value: ${rawValue}`);
}

// =============================================
// Extract machine type, number and shift from
// the "User" column value.
// Returns null for rows that represent the
// system "User" placeholder or unknown formats.
// =============================================
export function parseMachineIdentity(
  userValue: string
): ParsedMachineIdentity | null {
  if (!userValue || userValue === "User") {
    return null;
  }

  const match = userValue.match(USER_COLUMN_PATTERN);
  if (!match) {
    return null;
  }

  const rawType = match[1];
  const machineType = normaliseMachineTypeForEnum(rawType);

  return {
    machineType,
    machineNumber: parseInt(match[2], 10),
    shift: match[3].toUpperCase() === "AM" ? "Day" : "Night",
  };
}

// =============================================
// Apply the outlier cap to a raw seconds value.
// Operations longer than the threshold are
// capped to a representative per-part value.
// =============================================
function sanitiseDuration(rawSeconds: number): number {
  if (rawSeconds >= OUTLIER_THRESHOLD_SECONDS) {
    return MAX_SECONDS_PER_PART;
  }

  return rawSeconds;
}

// =============================================
// Map a single raw Excel row to a ParsedOperation.
// Returns null when the row cannot be mapped
// (invalid user format or invalid date).
// =============================================
export function mapRowToOperation(row: RawExcelRow): ParsedOperation | null {
  const identity = parseMachineIdentity(String(row["User"] ?? ""));
  if (!identity) {
    return null;
  }

  let startTime: string;
  let endTime: string;

  try {
    startTime = parseDateValue(row["Start date"]);
    endTime = parseDateValue(row["End date"]);
  } catch {
    return null;
  }

  const durationSeconds = sanitiseDuration(Number(row["Seconds"]) || 0);

  return {
    machineType: identity.machineType,
    machineNumber: identity.machineNumber,
    shift: identity.shift,
    workOrder: String(row["Work Order"] ?? "").trim(),
    productName: String(row["Product name"] ?? "").trim(),
    partName: String(row["Part name"] ?? "").trim(),
    startTime,
    endTime,
    durationSeconds,
  };
}

// =============================================
// Parse a full Excel file and return the list
// of ParsedOperations alongside the count of
// rows that could not be mapped.
// =============================================
export interface ExcelParseResult {
  operations: ParsedOperation[];
  rejectedRowCount: number;
}

export function parseExcelFileToOperations(file: File): Promise<ExcelParseResult> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onerror = () => {
      reject(new Error("Failed to read the uploaded file."));
    };

    reader.onload = (event) => {
      try {
        const buffer = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(buffer, { type: "array" });
        const firstSheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[firstSheetName];
        const rows = XLSX.utils.sheet_to_json<RawExcelRow>(sheet);

        if (rows.length === 0) {
          reject(new Error("The spreadsheet contains no data rows."));
          return;
        }

        const operations: ParsedOperation[] = [];
        let rejectedRowCount = 0;

        for (const row of rows) {
          const operation = mapRowToOperation(row);
          if (operation === null) {
            rejectedRowCount += 1;
          } else {
            operations.push(operation);
          }
        }

        resolve({ operations, rejectedRowCount });
      } catch (error) {
        reject(error);
      }
    };

    reader.readAsArrayBuffer(file);
  });
}
